import { useEffect, useState } from "react";

import { Button } from "../base/Button";
import LoadingWheel from "../shared/LoadingWheel";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import { useInitialPriceEstimateAiPrompt } from "../../data/InitialPriceEstimate/useInitialPriceEstimate";
import useInitialPriceEstimateMutations from "../../data/InitialPriceEstimate/useInitialPriceEstimateMutations";
import { MAX_AI_PROMPT_LENGTH } from "../../models/Category/InitialPriceEstimate";

/**
 * The default AI match prompt: what the lookup's AI check is told for any category without a
 * prompt of its own. Saving the built-in text unchanged, or blank, goes back to the built-in.
 */
export default function AiPromptModal({ open, setOpen }: ModalControlProps) {
  const { aiPrompt } = useInitialPriceEstimateAiPrompt(open);
  const { saveAiPrompt } = useInitialPriceEstimateMutations();
  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (!open || !aiPrompt.data) return;

    setDraft(aiPrompt.data.prompt);
  }, [open, aiPrompt.data]);

  const builtIn = aiPrompt.data?.builtInPrompt ?? "";
  const draftIsBuiltIn = draft.trim() === builtIn.trim();

  const handleSave = () => {
    saveAiPrompt.mutate(
      { prompt: draft },
      {
        onSuccess: () => setOpen(false),
      },
    );
  };

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-3xl">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">AI prompt</h2>
          <p className="mt-1 text-sm text-gray-500">
            After each eBay search the AI is asked which listings really are
            the part for the donor vehicle; the rest are left out of the price.
            This is what it is told, for every category that has no prompt of
            its own. Live on the next lookup; no category sync needed.
          </p>
        </div>

        {aiPrompt.isLoading ? (
          <div className="flex justify-center py-10">
            <LoadingWheel />
          </div>
        ) : aiPrompt.isError ? (
          <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {(aiPrompt.error as Error)?.message || "Could not load the AI prompt."}
          </p>
        ) : (
          <>
            {aiPrompt.data?.usingBuiltIn && draftIsBuiltIn && (
              <p className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                This is the built-in prompt. Saving a change stores your copy.
              </p>
            )}

            <label className="flex flex-col space-y-1">
              <span className="text-sm font-medium text-black opacity-80">
                Default prompt
              </span>
              <textarea
                value={draft}
                rows={18}
                maxLength={MAX_AI_PROMPT_LENGTH}
                onChange={(event) => setDraft(event.target.value)}
                className="w-full rounded border border-orange-600 px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-orange-600"
              />
            </label>
            <p className="text-xs text-gray-500">
              The vehicle, the part category, the search and the numbered
              listing titles are added for every check, so write the rules, not
              the facts. {draft.length.toLocaleString()} /{" "}
              {MAX_AI_PROMPT_LENGTH.toLocaleString()} characters.
            </p>

            {!draftIsBuiltIn && (
              <Button variant="outlined" size="sm" onClick={() => setDraft(builtIn)}>
                Reset to the built-in prompt
              </Button>
            )}
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            disabled={saveAiPrompt.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveAiPrompt.isPending || aiPrompt.isLoading || !aiPrompt.data}
          >
            {saveAiPrompt.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
